export function normalizeSlideIndex(index, count) {
  if (!count) return 0;
  return ((index % count) + count) % count;
}

export function shouldRunTestimonialAutoplay(state) {
  return Boolean(
    state.autoplay
      && state.slideCount > 1
      && state.sectionVisible
      && state.documentVisible,
  );
}

const elementName = 'aceii-testimonial-carousel';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiTestimonialCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.slides = [...this.querySelectorAll('[data-testimonial-slide]')];
      this.dots = [...this.querySelectorAll('[data-testimonial-dot]')];
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(
        1000,
        Number.parseFloat(this.dataset.intervalMs) || 7600,
      );
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active')),
      );
      this.sectionVisible = false;
      this.documentVisible = !document.hidden;
      this.timer = 0;
      this.animationFrame = 0;

      if (!this.slides.length) return;

      const { signal } = this.abortController;

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            const index = Number.parseInt(dot.dataset.testimonialDot, 10);
            this.setSlide(index, true);
          },
          { signal },
        );
      });

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          this.syncAutoplay();
        },
        { signal },
      );

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          this.syncAutoplay();
        },
        {
          rootMargin: '120px 0px',
          threshold: 0.05,
        },
      );
      this.observer.observe(this);

      this.setSlide(this.currentIndex, false);
      this.syncAutoplay();
    }

    disconnectedCallback() {
      this.clearAutoplay();
      this.observer?.disconnect();
      this.abortController?.abort();
      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
      }
      this.animationFrame = 0;
      this.abortController = null;
    }

    setSlide(index, restartAutoplay = true) {
      this.currentIndex = normalizeSlideIndex(index, this.slides.length);

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      if (restartAutoplay) this.syncAutoplay();
    }

    syncAutoplay() {
      this.clearAutoplay();

      if (
        !shouldRunTestimonialAutoplay({
          autoplay: this.autoplay,
          slideCount: this.slides.length,
          sectionVisible: this.sectionVisible,
          documentVisible: this.documentVisible,
        })
      ) {
        return;
      }

      this.restartProgress();
      this.timer = window.setTimeout(() => {
        this.setSlide(this.currentIndex + 1, true);
      }, this.interval);
    }

    clearAutoplay() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = 0;
      }

      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = 0;
      }

      this.dots.forEach((dot) => dot.classList.remove('is-progressing'));
    }

    restartProgress() {
      this.animationFrame = window.requestAnimationFrame(() => {
        this.animationFrame = 0;
        this.dots[this.currentIndex]?.classList.add('is-progressing');
      });
    }
  }

  window.customElements.define(elementName, AceiiTestimonialCarousel);
}
